import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import AnimeCard from "@/components/feature/AnimeCard";
import { useSeo, setJsonLd } from "@/hooks/useSeo";
import { animeList, categories } from "@/mocks/anime";

const sorts = [
  { key: "hot", label: "人气最高" },
  { key: "score", label: "评分最高" },
  { key: "new", label: "最新上线" },
];

export default function List() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "";
  const q = params.get("q") || "";
  const [sort, setSort] = useState("hot");
  const [input, setInput] = useState(q);

  useSeo({
    title: cat
      ? `${cat}番剧大全 - 免费在线观看全集与高清下载 | 奶水动漫`
      : "全部番剧 - 热门动画免费在线观看全集与高清下载 | 奶水动漫",
    description:
      "浏览奶水动漫全部分类番剧，涵盖热血、奇幻、科幻、恋爱、悬疑、古风等多类型动画，支持在线观看全集与高清资源下载。",
    keywords: "奶水动漫,番剧大全,动漫在线,动漫下载,动画资源",
  });

  const filtered = useMemo(() => {
    let list = [...animeList];
    if (cat) list = list.filter((a) => a.category === cat);
    if (q) {
      const kw = q.toLowerCase();
      list = list.filter(
        (a) =>
          a.title.toLowerCase().includes(kw) ||
          a.category.includes(kw) ||
          a.tags.some((t) => t.toLowerCase().includes(kw))
      );
    }
    if (sort === "score") list.sort((a, b) => b.score - a.score);
    if (sort === "new") list.sort((a, b) => b.year - a.year);
    else list.sort((a, b) => Number(b.views.replace("万", "")) - Number(a.views.replace("万", "")));
    return list;
  }, [cat, q, sort]);

  const setCat = (c: string) => {
    const next = new URLSearchParams(params);
    if (c) next.set("cat", c);
    else next.delete("cat");
    setParams(next);
  };

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = input.trim();
    const next = new URLSearchParams(params);
    if (kw) next.set("q", kw);
    else next.delete("q");
    setParams(next);
  };

  setJsonLd("ld-list", {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "奶水动漫番剧列表",
    description: "奶水动漫收录的热门动画大全，支持免费在线观看与高清下载。",
  });

  return (
    <div className="min-h-screen bg-background-950">
      <Navbar />
      <main className="pt-16">
        <div className="border-b border-background-800 bg-background-900/40">
          <div className="container-site py-8">
            <nav className="mb-4 flex items-center gap-1 text-xs text-foreground-500" aria-label="面包屑">
              <Link to="/" className="transition-colors hover:text-primary-400">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-300">全部番剧</span>
            </nav>
            <h1 className="font-heading text-2xl text-foreground-50 md:text-3xl">
              {cat ? `${cat}番剧大全` : "全部番剧"}
            </h1>
            <p className="mt-2 text-sm text-foreground-400">
              共收录 <strong className="text-primary-400">{filtered.length}</strong> 部，支持在线观看全集与高清下载
            </p>

            <form onSubmit={onSearch} className="mt-5 flex max-w-md items-center gap-2 rounded-full border border-background-700 bg-background-900 px-4">
              <i className="ri-search-line text-foreground-400" />
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="搜索番名或标签…"
                className="w-full bg-transparent py-2.5 text-sm text-foreground-100 outline-none placeholder:text-foreground-500"
              />
              <button type="submit" className="shrink-0 text-sm font-semibold text-primary-400">搜索</button>
            </form>
          </div>
        </div>

        <div className="container-site py-8">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setCat("")}
              className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                !cat ? "bg-primary-500 text-background-950" : "border border-background-700 text-foreground-300 hover:border-primary-500 hover:text-primary-400"
              }`}
            >
              全部
            </button>
            {categories.map((c) => (
              <button
                key={c.name}
                onClick={() => setCat(c.name)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                  cat === c.name ? "bg-primary-500 text-background-950" : "border border-background-700 text-foreground-300 hover:border-primary-500 hover:text-primary-400"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-background-800 pt-5">
            <span className="text-xs text-foreground-500">排序：</span>
            {sorts.map((s) => (
              <button
                key={s.key}
                onClick={() => setSort(s.key)}
                className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                  sort === s.key ? "bg-secondary-500/80 text-background-950" : "bg-background-900 text-foreground-300 hover:bg-background-800"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="mt-12 flex flex-col items-center py-16 text-center">
              <i className="ri-inbox-2-line text-5xl text-background-700" />
              <p className="mt-4 font-bold text-foreground-200">没有找到匹配的番剧</p>
              <p className="mt-1 text-sm text-foreground-500">换个关键词或分类再试试吧</p>
              <button onClick={() => { setParams({}); setInput(""); }} className="btn-ghost mt-5">清空筛选</button>
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 md:gap-4">
              {filtered.map((a) => (
                <AnimeCard key={a.id} anime={a} />
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}